import * as z from 'zod';
import { Lot, LotCreate } from 'src/services/lots';
import { Product, ProductIn } from 'src/services/productsNew';
import { ProductPackaging } from 'src/services/productPackaging';
import { QualityCheck, QualityCheckIn } from 'src/services/qualityCheck';
import { t } from 'i18next';
import { PRODUCT_IMAGE_TYPES, PRODUCT_IMAGE_MAX_FILE_SIZE_BYTES } from './ProductImage/ProductImage';

const PRODUCT_ATTACHMENT_TYPES = ['application/pdf'];
const PRODUCT_ATTACHMENT_MAX_FILE_SIZE_BYTES = 20 * 1024 * 1024; // 20MB

const barcodeSchema = z.string().optional();

const qualityCheckSchema = z.object({
  id: z.number(),
  lastQualityCheckDate: z.string().optional(),
  productId: z.number(),
  qualityCheckEnabled: z.boolean(),
  qualityCheckInstruction: z.string(),
  qualityCheckInterval: z.enum(['daily', 'weekly', 'every 2 weeks', 'monthly', 'new_batch']).optional(),
  qualityCheckName: z.string().min(1).max(20),
});

export const qualityChecksSchema = z.object({
  qualityChecks: z.array(qualityCheckSchema),
});

const tagSchema = z.object({
  id: z.number(),
  color: z.number().nullable(),
  displayName: z.string().nullable(),
});

const attachmentSchema = z.object({
  id: z.number().nullable().optional(),
  mimetype: z.string(),
  name: z.string(),
  file: z
    .instanceof(File)
    .optional()
    .nullable()
    .refine(file => !file || PRODUCT_ATTACHMENT_TYPES.includes(file.type), {
      message: 'File type must be .pdf',
    })
    .refine(file => !file || file.size <= PRODUCT_ATTACHMENT_MAX_FILE_SIZE_BYTES, {
      message: `File size must be less than ${PRODUCT_ATTACHMENT_MAX_FILE_SIZE_BYTES / 1024 / 1024}MB`,
    }),
});

const packagingSchema = z.object({
  id: z.number(),
  barcode: barcodeSchema,
  name: z.string().min(1),
  quantity: z.coerce.number().positive().int(),
});

export const lotSchema = z.object({
  id: z.number(),
  // No length requirement: the merchant may not know the lot at inbound time.
  // An empty name means "no lot" — never persisted (see LotOverlay's Save handler).
  name: z.string(),
  removalDate: z.string().optional().nullable(),
  isLocked: z.boolean(),
  quantity: z.coerce.number().int(),
});

export const lotsSchema = z.object({
  lots: z.array(lotSchema),
});

export type QualityCheckFormValues = z.infer<typeof qualityCheckSchema>;
export type QualityChecksFormValues = z.infer<typeof qualityChecksSchema>;
export type AttachmentFormValues = z.infer<typeof attachmentSchema>;
export type PackagingFormValues = z.infer<typeof packagingSchema>;
export type LotFormValues = z.infer<typeof lotSchema>;
export type LotsFormValues = z.infer<typeof lotsSchema>;

export function createProductFormSchema() {
  // this is a function so we can pass in the t translation function in the future

  const floatSchema = z.coerce.number().refine(value => Number.isFinite(value), 'Must be a valid number');

  return z
    .object({
      name: z.string().min(1, 'Name is required'), // todo translate message
      image: z
        .instanceof(File)
        .nullable()
        .optional()
        .refine(file => !file || PRODUCT_IMAGE_TYPES.includes(file.type), {
          message: 'File type must be .png, .jpeg, .bmp or .gif', // todo translate message
        })
        .refine(file => !file || file.size <= PRODUCT_IMAGE_MAX_FILE_SIZE_BYTES, {
          message: `File size must be less than ${PRODUCT_IMAGE_MAX_FILE_SIZE_BYTES / 1024 / 1024}MB`, // todo translate message
        }),
      isNonShippable: z.boolean().optional(),
      isProductSet: z.boolean().optional(),
      articleNumber: z.string().optional(),
      barcode: barcodeSchema,
      // Prices are optional at field level; they are only required for
      // non-set products (enforced in the superRefine below). Set products
      // hide the price inputs, so they must not be mandatory.
      standardPrice: z.coerce.number().optional(),
      listPrice: z.coerce.number().optional(),
      productStatus: z
        .enum(['Draft', 'Confirmed'], {
          required_error: 'Status is required',
        })
        .nullable()
        .optional(),
      length: floatSchema,
      width: floatSchema,
      height: floatSchema,
      countryOfOrigin: z.string().optional().nullable(),
      nettoWeight: floatSchema.optional().nullable(),
      weight: floatSchema,
      qualityCheckEnabled: z.boolean(),
      qualityChecks: z.array(qualityCheckSchema).optional().nullable(),
      hsCode: z.string().optional().nullable(),
      tags: z.array(tagSchema).optional().nullable(),
      tracking: z.enum(['serial', 'lot', 'none']).optional().nullable(),
      useExpirationDate: z.boolean().optional().nullable(),
      removalTime: z.coerce.number().int().optional().nullable(),
      attachments: z.array(attachmentSchema).max(50, 'Too large amount of attachments').optional().nullable(),
      productPackagings: z.array(packagingSchema).optional().nullable(),
      customsDescription: z.string().optional(),
    })
    .refine(data => !data.nettoWeight || data.nettoWeight < data.weight, {
      message: t('errors.netto_weight_error'),
      path: ['nettoWeight'],
    })
    .superRefine((data, ctx) => {
      // Set products don't carry their own prices (the inputs are hidden), so
      // skip the required-price validation for them. Everything else still
      // needs a valid standard/list price.
      if (data.isProductSet) return;
      if (data.standardPrice == null || !Number.isFinite(data.standardPrice)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Must be a valid number',
          path: ['standardPrice'],
        });
      }
      if (data.listPrice == null || !Number.isFinite(data.listPrice)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Must be a valid number',
          path: ['listPrice'],
        });
      }
    });
}

export type ProductFormValues = z.infer<ReturnType<typeof createProductFormSchema>>;

export function formValuesToQualityChecks(values: ProductFormValues['qualityChecks']): QualityCheck[] {
  const res: QualityCheck[] = (values ?? [])?.map(c => ({
    id: c.id,
    last_quality_check_date: c.lastQualityCheckDate ? c.lastQualityCheckDate : undefined,
    product_id: c.productId,
    quality_check_enabled: c.qualityCheckEnabled,
    quality_check_instruction: c.qualityCheckInstruction,
    quality_check_interval: c.qualityCheckInterval ?? undefined,
    quality_check_name: c.qualityCheckName,
  }));
  return res;
}

export function formValuesToQualityCheckIns(values: ProductFormValues['qualityChecks']): QualityCheckIn[] {
  const res: QualityCheckIn[] = (values ?? [])?.map(c => ({
    last_quality_check_date: c.lastQualityCheckDate ? c.lastQualityCheckDate : undefined,
    quality_check_enabled: c.qualityCheckEnabled,
    quality_check_instruction: c.qualityCheckInstruction,
    quality_check_interval: c.qualityCheckInterval ?? undefined,
    quality_check_name: c.qualityCheckName,
  }));
  return res;
}

export function formValuesToProductPackagings(values: ProductFormValues['productPackagings']): ProductPackaging[] {
  const res: ProductPackaging[] = (values ?? [])?.map(p => ({
    id: p.id,
    barcode: p.barcode,
    name: p.name,
    quantity: p.quantity,
  }));
  return res;
}

export function formValuesToProduct(values: ProductFormValues): ProductIn {
  return {
    name: values.name,
    is_non_shippable: values.isNonShippable ?? false,
    article_number: values.articleNumber,
    barcode: values.barcode,
    standard_price: values.standardPrice ?? 0,
    list_price: values.listPrice ?? 0,
    product_status: values.productStatus ?? undefined,
    length: values.length,
    width: values.width,
    height: values.height,
    country_of_origin: values.countryOfOrigin ?? undefined,
    weight: values.weight,
    netto_weight: values.nettoWeight ?? undefined,
    quality_check_enabled: values.qualityCheckEnabled,
    hs_code: values.hsCode ?? undefined,
    // tags: values.tags ?? undefined,
    tracking: values.tracking ?? null,
    use_expiration_date: values.useExpirationDate ?? null,
    removal_time: values.removalTime ?? undefined,
    customs_description: values.customsDescription ?? undefined,
    active: null,
    chat: null,
    attachments: values.attachments?.map(a => ({
      id: a.id,
      mimetype: a.mimetype,
      name: a.name,
    })),
  };
}

// is_product_set is not part of the generated ProductIn/ProductUpdateIn yet —
// regenerate via `yarn generate:api-code` once the backend change is deployed.
export type ProductUpdateOut = ProductIn & { is_product_set?: boolean };

export function formValuesToProductUpdate(values: ProductFormValues, previous: ProductFormValues): ProductUpdateOut {
  const product: ProductUpdateOut = formValuesToProduct(values);
  // The "set" type is not a real backend field — it is derived from the
  // product's BoMs. Sending is_product_set=false makes the backend delete
  // them; otherwise the refetch re-derives the type as "set" and the
  // switch silently reverts.
  if (previous.isProductSet && !values.isProductSet) {
    product.is_product_set = false;
  }
  return product;
}

export function createEmptyProduct(): ProductIn {
  return {
    name: '',
    is_non_shippable: false,
    standard_price: 0,
    list_price: 0,
    length: 0,
    width: 0,
    height: 0,
    weight: 0,
    quality_check_enabled: false,
    use_expiration_date: false,
    active: null,
    chat: null,
    tracking: null,
  };
}

export function qualityCheckToFormValues(check: QualityCheck): QualityCheckFormValues {
  return {
    id: check.id as number,
    lastQualityCheckDate: check.last_quality_check_date ?? undefined,
    productId: check.product_id as number,
    qualityCheckEnabled: check.quality_check_enabled ?? false,
    qualityCheckInstruction: (check.quality_check_instruction as string) ?? '',
    qualityCheckInterval: (check.quality_check_interval as QualityCheckFormValues['qualityCheckInterval']) ?? undefined,
    qualityCheckName: check.quality_check_name as string,
  };
}

export function productToFormValues(product: Product): ProductFormValues {
  const transformedQualityChecks = (product.quality_checks as any[])?.map(check => qualityCheckToFormValues(check));

  const transformedAttachments = (product.attachments as any[])?.map(attachment => {
    const transformed: AttachmentFormValues = {
      id: attachment.id,
      name: attachment.name,
      mimetype: attachment.mimetype,
    };
    return transformed;
  });

  const transformedPackagings = (product.product_packagings as any[])?.map(packaging => {
    const transformed: PackagingFormValues = {
      id: packaging.id,
      name: packaging.name,
      barcode: packaging.barcode,
      quantity: packaging.quantity,
    };
    return transformed;
  });
  const isProductSet =
    product?.bom_components &&
    product?.bom_components?.length > 0 &&
    !product.product_packagings?.length &&
    !product.is_non_shippable;

  return {
    name: product.name,
    isNonShippable: product.is_non_shippable,
    isProductSet: !!isProductSet,
    articleNumber: (product.article_number as any) ?? undefined,
    barcode: (product.barcode as any) ?? undefined,
    length: product.length as any,
    width: product.width as any,
    height: product.height as any,
    weight: product.weight,
    standardPrice: product.standard_price,
    listPrice: product.list_price,
    productStatus: (product.product_status as any) ?? undefined,
    countryOfOrigin: (product.country_of_origin as any) ?? undefined,
    nettoWeight: (product.netto_weight as any) ?? undefined,
    qualityCheckEnabled: !!product.quality_check_enabled,
    qualityChecks: transformedQualityChecks ?? [],
    hsCode: (product.hs_code as any) ?? undefined,
    tracking: product.tracking as any,
    useExpirationDate: product.use_expiration_date as any,
    attachments: transformedAttachments,
    productPackagings: transformedPackagings,
    customsDescription: (product.customs_description as any) ?? undefined,
  };
}

export function lotToFormValues(lot: Lot): LotFormValues {
  return {
    id: lot.id as any,
    name: lot.name,
    removalDate: lot.removal_date ?? null,
    isLocked: lot.is_locked,
    quantity: lot.quantity,
  };
}

export function formValuesToLot(values: LotFormValues): LotCreate {
  const res: LotCreate = {
    name: values.name,
    removal_date: values.removalDate as any,
    is_locked: values.isLocked,
  };
  return res;
}
